import React from 'react'
import styled from 'styled-components'
import {
  AddressDTO,
  useConfigState,
  useGlobalLabels,
  useRemoveAddress,
  useUIFunctions,
  useShippingAddress,
} from '@sentecacommerce-theme/lib'

import { OfficePreview, AddressPreviewGeneric } from './Components'
import { IconEdit, IconClose, Typography } from '../..'

import { AddressFormModalProps } from '../../Modals'

export const AddressBookPreview = ({
  data,
  isSelectable,
  isSelected,
  onChange,
  hideRemove,
}: AddressBookPreviewProps) => {
  const [remove] = useRemoveAddress()
  const {
    address,
    globalValidations,
    edit,
    nr,
    remove: removeBtnLabel,
  } = useGlobalLabels()
  const { enqueueView } = useUIFunctions()
  const formattedAddress = useShippingAddress(data)
  const { views } = useConfigState()

  const handleRemoveClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    event.stopPropagation()

    data._id &&
      remove({
        id: data._id,
      })
  }

  const mod = data.moduleData?.[0]

  const openEditForm = () => {
    enqueueView<AddressFormModalProps>(views.addressForm, {
      address: data,
      errorLabels: globalValidations || {},
    })
  }

  return (
    <Container
      isSelected={isSelected}
      isSelectable={isSelectable}
      onClick={() => onChange?.(data._id as string)}
    >
      <MainInfoContainer hideRemove={Boolean(hideRemove)}>
        {isSelectable && (
          <CheckContainer>
            <Check isSelected={isSelected} />
          </CheckContainer>
        )}
        <Name hideRemove={Boolean(hideRemove)}>
          {data.firstName} {data.lastName}
        </Name>
        <AddressContainer hideRemove={Boolean(hideRemove)}>
          {formattedAddress?.isOffice && (
            <OfficePreview office={formattedAddress?.office} />
          )}
          {formattedAddress?.isAddress && (
            <AddressPreviewGeneric data={formattedAddress} />
          )}
        </AddressContainer>

        <CourierContainer hideRemove={Boolean(hideRemove)}>
          <CourierLabel>{`${address?.courier}`}</CourierLabel>{' '}
          <Medium>{mod?.moduleKey}</Medium>
        </CourierContainer>
      </MainInfoContainer>
      <ButtonsContainer>
        {!hideRemove && (
          <SingleIconContainer onClick={handleRemoveClick}>
            <StyledBtnTitle>{removeBtnLabel}</StyledBtnTitle>
            <IconClose />
          </SingleIconContainer>
        )}
        {!hideRemove && (
          <SingleIconContainer onClick={openEditForm}>
            <StyledBtnTitle>{edit}</StyledBtnTitle>
            <IconEdit />
          </SingleIconContainer>
        )}
      </ButtonsContainer>
    </Container>
  )
}

type Props = {
  isSelectable?: boolean
  isSelected?: boolean
  onChange?: (id: string) => void
  hideRemove?: boolean
  withEdit?: boolean
}

export type AddressBookPreviewProps = Props & {
  data: AddressDTO
}

export const Cap = styled(Typography)`
  text-transform: capitalize;
`

export const Medium = styled(Typography)`
  font-weight: 500;
`

export const MainInfoContainer = styled.div.attrs({
  className: 'd-flex justify-content-between',
})<{ hideRemove: boolean }>`
  flex-direction: column;
  font-weight: 300;
  padding-left: 2rem;
  width: ${({ hideRemove }) => (hideRemove ? '100%' : '70%')};
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    flex-direction: row;
  }
`

export const Container = styled.div.attrs({
  className: 'd-flex justify-content-between py-4 px-4 mb-4',
})<Props & { isSelected?: boolean }>`
  position: relative;
  flex-direction: column;
  border: 0.1rem solid ${({ theme }) => theme.palette.grey};
  width: 100%;
  min-height: 10rem;
  ${(props) =>
    props.isSelectable &&
    `
      cursor: pointer;
      padding-left: 3rem;
      ${
        !props.isSelected &&
        `
        &:hover {
          opacity: 0.7;
        }
      `
      }
  `}
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    flex-direction: row;
  }
`

export const CheckContainer = styled.div`
  position: absolute;
  top: 1.3rem;
  left: 1rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    position: relative;
    top: -0.1rem;
    left: 0;
    width: 5%;
  }
`

export const Check = styled.div<{ isSelected?: boolean }>`
  width: 1.6rem;
  height: 1.6rem;
  border: 0.1rem solid ${({ theme }) => theme.palette.silverTree};
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  left: 0;
  top: 0.5rem;

  ${(props) =>
    props.isSelected &&
    `
      &:after {
        content: '';
        display: block;
        width: 1rem;
        height: 1rem;
        border-radius: 50%;
        background: ${props.theme.palette.silverTree};
      }
  `}
`
export const ButtonsContainer = styled.div.attrs({
  className: 'd-flex align-items-center align-items-md-end',
})`
  cursor: pointer;
  justify-content: flex-end;
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    position: absolute;
    flex-direction: column;
    top: 0.3rem;
    right: 1.5rem;
  }
`
export const SingleIconContainer = styled.div.attrs({
  className: 'd-flex align-items-center mt-3 mr-2 mb-md-2',
})`
  & svg {
    width: 1rem;
    margin-left: 0.5rem;
  }
`

export const StyledBtnTitle = styled(Typography).attrs({ variant: '8' })`
  text-decoration: underline;
`
export const Name = styled(Typography)<{ hideRemove: boolean }>`
  line-height: 1.6;
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    width: ${({ hideRemove }) => (hideRemove ? '30%' : '33.333%')};
  }
`
export const AddressContainer = styled.div<{ hideRemove: boolean }>`
  margin: 1rem 0;
  div + div {
    margin-top: 0.5rem;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    margin: 0;
    width: ${({ hideRemove }) => (hideRemove ? '40%' : '33.333%')};
  }
`
export const CourierLabel = styled(Typography)``

export const CourierContainer = styled.div.attrs({ className: 'd-flex' })<{
  hideRemove: boolean
}>`
  margin-top: 0.5rem;
  ${CourierLabel} {
    display: none;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    width: ${({ hideRemove }) => (hideRemove ? '20%' : '33.333%')};
    margin-top: 0;
    ${Typography} {
      margin-left: 0.5rem;
    }
    ${CourierLabel} {
      display: block;
    }
  }
`
export const OfficeContainer = styled.div.attrs({ className: 'd-flex' })`
  flex-direction: column;
  ${Medium} {
    display: none;
  }
  margin: 0.5rem 0;
  @media (min-width: ${({ theme }) => theme.breakpoints.md.min}) {
    flex-direction: row;
    width: 40%;
    margin: 0;
    ${Typography} {
      margin-right: 0.5rem;
    }
    ${Medium} {
      display: block;
    }
    margin-top: 0;
  }
`
export const OfficeOrAddressContainer = styled.div`
  line-height: 1.6;
`
export const Ellipsis = styled(Typography)`
  max-width: 20rem;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`
